import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ColumnDef } from '@tanstack/react-table';
import { Users } from 'lucide-react';
import { cn } from '@r/utils/utils.ts';
import { DataTable } from '@r/components/data-table/data-table.tsx';
import { DataTableSkeleton } from '@r/components/data-table/data-table-skeleton.tsx';
import { DataTableAdvancedToolbar } from '@r/components/data-table/advanced/data-table-advanced-toolbar.tsx';
import { useDataTable } from '@r/hooks/use-data-table.ts';
import { UserFullname } from '@r/components/user-fullname.tsx';
import {
  useApiAdminsegmentsIdmembersGetCollection,
  getApiAdminsegmentsIdmembersGetCollectionQueryKey
} from '@r/api/generated/client.ts';
import type { UserWorkspaceSegmentMemberReadModel } from '@r/api/generated/client.schemas.ts';
import { useOrvalQueryErrorToast } from '@r/hooks/use-orval-error-toast.ts';

type Member = UserWorkspaceSegmentMemberReadModel;

interface SegmentMembersTableProps {
  segmentId: number;
  onExport?: () => void;
  isExporting?: boolean;
}

/** Affiche le tableau paginé des utilisateurs correspondant à un segment, avec export optionnel. */
export function SegmentMembersTable({
  segmentId,
  onExport,
  isExporting
}: SegmentMembersTableProps) {
  const { t } = useTranslation();
  const [apiPagination, setApiPagination] = useState({ pageIndex: 0, pageSize: 30 });

  const params = { page: apiPagination.pageIndex + 1, perPage: apiPagination.pageSize };

  const membersQuery = useApiAdminsegmentsIdmembersGetCollection(segmentId, params, {
    query: {
      queryKey: getApiAdminsegmentsIdmembersGetCollectionQueryKey(segmentId, params)
    }
  });
  useOrvalQueryErrorToast(membersQuery, t('toast.error.label'));

  const columns = useMemo<ColumnDef<Member>[]>(
    () => [
      {
        id: 'fullname',
        header: t('admin.segments.members.column.fullname'),
        cell: ({ row }) => (
          <UserFullname
            fullname={row.original.user.fullname}
            isDeleted={row.original.user.isDeleted}
          />
        )
      },
      {
        id: 'email',
        header: t('admin.segments.members.column.email'),
        cell: ({ row }) => row.original.user.email
      },
      {
        id: 'uniqueCode',
        header: t('admin.segments.members.column.unique.code'),
        cell: ({ row }) => row.original.uniqueCode ?? '—'
      }
    ],
    [t]
  );

  const data = useMemo(() => membersQuery.data?.data ?? [], [membersQuery.data]);
  const pagination = membersQuery.data?.pagination;

  const mappedTable = useDataTable<Member, unknown>({
    data,
    columns,
    pageCount: pagination?.last ?? 1,
    enableAdvancedFilter: false,
    manualPaginate: true,
    manualSort: false,
    manualFilter: false
  });

  const { pageIndex, pageSize } = mappedTable.table.getState().pagination;

  useEffect(() => {
    if (pageIndex !== apiPagination.pageIndex || pageSize !== apiPagination.pageSize) {
      setApiPagination({ pageIndex, pageSize });
    }
  }, [pageIndex, pageSize]);

  if (membersQuery.isLoading) {
    return <DataTableSkeleton columnCount={3} rowCount={5} />;
  }

  return (
    <div className={cn('transition-opacity', membersQuery.isFetching && 'opacity-60')}>
      <DataTableAdvancedToolbar
        table={mappedTable.table}
        title={t('admin.segments.members.title')}
        titleIcon={<Users className="w-5 h-5" />}
        onExport={onExport}
        isExporting={isExporting}
      />
      <DataTable
        table={mappedTable.table}
        totalItems={pagination?.totalItems}
        emptyIcon={Users}
        emptyMessageKey="admin.segments.members.empty"
      />
    </div>
  );
}
